/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Drawer, Button, Row, Col } from "@duxton-web/ui"
import { AppState } from "store/store.type"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { ServiceHourList } from "components/list/ServiceHourList"
import { SellingTime } from "types/models/menu"
import { SellingTimeDropdown } from "components/dropdowns"
import { getSellingTimeMetadata } from "utils/selling_time_helper"
import { EventAction, EventCategory } from "constants/tracking"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import notification from "components/common/notification"
import { useMenuItemSearch } from "hooks/useMenuItemSearch"
import { useSellingTimes } from "hooks/useSellingTimes"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import {
  resolveV3SelectionForAPI,
  computeV3SelectionSummary,
} from "utils/bulk_selection_helpers"
import {
  flattenInfiniteHierarchies,
  useMenuV3HierarchiesInfinite,
  useUpdateItemsSellingTimeMutation,
} from "queries"
import { mapHierarchyEntitiesToDepartments } from "utils/menuV3Mapper"
import styles from "./MultiSelectSellingTimeDrawer.modules.css"

export interface MultiSelectSellingTimeDrawerV3Props {
  visible: boolean
  onClose: () => void
}

export const MultiSelectSellingTimeDrawerV3: FC<MultiSelectSellingTimeDrawerV3Props> = ({
  visible,
  onClose,
}: MultiSelectSellingTimeDrawerV3Props) => {
  const { sellingTimes } = useSellingTimes()
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu)
  const { isEditAllowed } = useMenuEditorStatus()
  const { refetchSearch } = useMenuItemSearch()
  const [updateItemsSellingTime] = useUpdateItemsSellingTimeMutation()

  const bulkSelectionsV3 = useSelector(
    (state: AppState) => state.bulkSelectionsV3,
  )

  // Read departments from RTK Query cache — no new network request, data is already loaded
  const { currentData: deptData } = useMenuV3HierarchiesInfinite(
    { merchantID, merchantGroupID },
    { skip: !merchantID },
  )
  const departments = useMemo(
    () =>
      mapHierarchyEntitiesToDepartments(
        flattenInfiniteHierarchies(deptData) ?? [],
      ),
    [deptData],
  )

  const { itemCount } = computeV3SelectionSummary(bulkSelectionsV3, departments)

  const textMap = useTranslationsMap({
    sellingTimeLabel: "menu.sellingTime.label.sellingTime",
    saveChangesButton: "menu.buttons.saveChanges",
    updateSuccessMessage: "menu.sellingTime.toast.success.update",
    cancelButton: "menu.buttons.cancel",
  })

  const drawerTitle = useTranslation(
    itemCount === 1
      ? "menu.overview.itemSelected"
      : "menu.overview.itemsSelected",
    { "<x>": `${itemCount}` },
  )

  /**
   * No default selection: V3 selections are hierarchical (dept/sub-dept with exclusions)
   * and per-item sellingTimeID is not in the hierarchy cache, making a shared default too
   * expensive to compute.
   *
   * No no-op guard needed: since selectedSellingTime always starts as undefined,
   * disabled={!selectedSellingTime} already blocks saving without a change.
   */
  const [selectedSellingTime, setSelectedSellingTime] = useState<
    SellingTime | undefined
  >()

  useEffect(() => {
    if (!visible) {
      setSelectedSellingTime(undefined)
    }
  }, [visible])

  const getSellingTimeInfoEventLabels = () => {
    const newSellingTime = selectedSellingTime
    return {
      SELLING_TIME_INFO: newSellingTime
        ? getSellingTimeMetadata(newSellingTime)
        : null,
    }
  }

  const {
    departmentIDs,
    subDepartmentIDs,
    itemIDs,
    excludedSubDepartmentIDs,
    excludedItemIDs,
  } = useMemo(() => resolveV3SelectionForAPI(bulkSelectionsV3), [
    bulkSelectionsV3,
  ])

  useEffect(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewModifyItem,
      action: EventAction.pageView,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        DEPARTMENT_ID: departmentIDs,
        SUB_DEPARTMENT_ID: subDepartmentIDs,
        ITEM_ID: itemIDs,
        EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
        EXCLUDED_ITEM_ID: excludedItemIDs,
        NUM_ITEM_SELECTED: itemCount,
        IS_PARTIAL_MENU: true,
      },
    })
  }, [
    departmentIDs,
    subDepartmentIDs,
    itemIDs,
    excludedSubDepartmentIDs,
    excludedItemIDs,
    merchantID,
    merchantGroupID,
    menuGroupID,
    itemCount,
  ])

  const handleClickSave = async () => {
    if (!selectedSellingTime?.sellingTimeID) return

    const { error } = await updateItemsSellingTime({
      merchantID,
      merchantGroupID,
      itemIDs,
      departmentIDs,
      subDepartmentIDs,
      excludedSubDepartmentIDs,
      excludedItemIDs,
      sellingTimeID: selectedSellingTime.sellingTimeID,
    })

    if (!error) {
      notification.success({ message: textMap.updateSuccessMessage })
      await refetchSearch()
      onClose()
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewModifyItem,
      action: EventAction.submitModifyItem,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ...getSellingTimeInfoEventLabels(),
        DEPARTMENT_ID: departmentIDs,
        SUB_DEPARTMENT_ID: subDepartmentIDs,
        ITEM_ID: itemIDs,
        EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
        EXCLUDED_ITEM_ID: excludedItemIDs,
        NUM_ITEM_SELECTED: itemCount,
        IS_PARTIAL_MENU: true,
        STATUS: error ? "FAILURE" : "SUCCESS",
        ...(error && getResponseErrorEventLabels(error)),
      },
    })
  }

  return (
    <Drawer
      title={drawerTitle}
      width={450}
      open={visible}
      onClose={onClose}
      destroyOnClose
    >
      <div className={styles.content}>
        <Row className={styles.contentRow}>
          <div className={styles.labelItem}>
            <div
              className={styles.label}
            >{`${textMap.sellingTimeLabel} *`}</div>
            <SellingTimeDropdown
              data-testid="selling-time-dropdown-v3"
              disabled={!isEditAllowed}
              onChange={setSelectedSellingTime}
              selectedSellingTime={selectedSellingTime}
              sellingTimes={sellingTimes}
              eventTrackerObject={{
                eventCategoryTracker: EventCategory.menuOverviewModifyItem,
                // itemIDs omitted: V3 uses hierarchical selection (dept/sub-dept),
                // so individual item IDs are not available here. Passing [] would
                // emit NUM_ITEM: 0 which is misleading.
                isMultipleSelect: true,
                isPartialMenu: true,
              }}
            />
          </div>
          <ServiceHourList sellingTime={selectedSellingTime} />
        </Row>
      </div>
      <div className={styles.footer}>
        <Row gutter={12}>
          <Col span={6}>
            <Button block onClick={onClose}>
              {textMap.cancelButton}
            </Button>
          </Col>
          <Col flex="auto">
            <Button
              data-testid="save-button-v3"
              type="primary"
              onClick={handleClickSave}
              block
              disabled={!selectedSellingTime}
            >
              {textMap.saveChangesButton}
            </Button>
          </Col>
        </Row>
      </div>
    </Drawer>
  )
}
